import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  SendHorizonal,
  Trash2,
  CheckCheck,
  Reply,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from "@/components/ui/Card";
import { cn } from "@/lib/cn";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface MessageRecord {
  id: number;
  agency_id: number;
  from_user_id: number;
  to_user_id: number;
  subject: string | null;
  body: string | null;
  read: number;
  request_reply: number;
  confirm_receipt: number;
  receipt_confirmed: number;
  created_at: string;
  from_first_name?: string;
  from_last_name?: string;
  to_first_name?: string;
  to_last_name?: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function SentMessages() {
  const queryClient = useQueryClient();
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const { data: sentData, isLoading } = useQuery<{ data: MessageRecord[] }>({
    queryKey: ["messaging", "sent"],
    queryFn: () => api.get<{ data: MessageRecord[] }>("/messaging/sent"),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => api.delete(`/messaging/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["messaging"] });
    },
  });

  const messages = sentData?.data ?? [];

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="h-16 animate-pulse rounded-lg border bg-muted"
          />
        ))}
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <SendHorizonal className="mx-auto h-10 w-10 text-muted-foreground" />
          <p className="mt-2 text-sm text-muted-foreground">
            No sent messages.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">
          Sent ({messages.length})
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="divide-y">
          {messages.map((msg) => (
            <div key={msg.id}>
              <button
                type="button"
                onClick={() =>
                  setExpandedId(expandedId === msg.id ? null : msg.id)
                }
                className="flex w-full items-center gap-4 px-4 py-3 text-left text-sm transition-colors hover:bg-accent/50"
              >
                <SendHorizonal className="h-4 w-4 shrink-0 text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate">
                      To: {msg.to_first_name ?? "Unknown"}{" "}
                      {msg.to_last_name ?? ""}
                    </span>
                    <span className="shrink-0 text-xs text-muted-foreground">
                      {new Date(msg.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="truncate text-muted-foreground">
                    {msg.subject || "(No subject)"}
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  {msg.request_reply === 1 && (
                    <span title="Reply requested"><Reply className="h-3.5 w-3.5 text-amber-500" /></span>
                  )}
                  {msg.confirm_receipt === 1 && (
                    <span title={msg.receipt_confirmed ? "Receipt confirmed" : "Awaiting receipt"}>
                      <CheckCheck
                        className={cn(
                          "h-3.5 w-3.5",
                          msg.receipt_confirmed
                            ? "text-green-500"
                            : "text-muted-foreground",
                        )}
                      />
                    </span>
                  )}
                  {expandedId === msg.id ? (
                    <ChevronUp className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  )}
                </div>
              </button>

              {expandedId === msg.id && (
                <div className="border-t bg-muted/30 px-4 py-3">
                  <div className="mb-2 flex items-center gap-2 text-xs text-muted-foreground">
                    <span>
                      To: {msg.to_first_name ?? "Unknown"}{" "}
                      {msg.to_last_name ?? ""}
                    </span>
                    <span>|</span>
                    <span>
                      {new Date(msg.created_at).toLocaleString()}
                    </span>
                    {msg.read === 1 && (
                      <>
                        <span>|</span>
                        <span className="text-green-600">Read</span>
                      </>
                    )}
                  </div>
                  <p className="whitespace-pre-wrap text-sm">{msg.body}</p>
                  <div className="mt-3 flex gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteMutation.mutate(msg.id);
                      }}
                    >
                      <Trash2 className="mr-1 h-3.5 w-3.5" />
                      Delete
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
